import React from "react";

import { isBrowser } from "@helpers/browser";
import { cn } from "@helpers/general";
import { withALErrorBoundary } from "@helpers/ErrorBoundary/ALErrorBoundary";
import { formatEngravingText } from "@components/ProductPage/mto/mtoEngraving";

import CurvedText from "./CurvedText";

// The artboard the dome curves are drawn on, and the viewBox the overlay carries
const DOME_ARTBOARD = { width: 445, height: 178 };

// Contentful authors the engraving sizes in `cqw`, a share of the preview width: that is what the
// overlays laid out in HTML need. Inside a viewBox a font-size resolves to CSS pixels and is then
// read as user units, so the same value would scale twice — with the container and again with the
// viewBox — and only land on the authored share on a preview exactly `DOME_ARTBOARD.width` wide:
// too small on a phone (and in the cart thumbnails), too big on a desktop column. Resolving the
// share against the artboard instead gives the text one size relative to the ring everywhere.
function toArtboardUnits(fontSize) {
  const share = /^\s*([\d.]+)cq[wi]\s*$/.exec(String(fontSize ?? ""));
  if (!share) return fontSize;

  const units = (parseFloat(share[1]) / 100) * DOME_ARTBOARD.width;
  return `${Math.round(units * 100) / 100}px`;
}

function filterDomByVariant(element, selectedVariantIds) {
  if (!element || !element.getAttribute) return;

  const variantId = element.getAttribute("data-variant");

  // Remove element entirely if its variant is not selected
  if (variantId && !selectedVariantIds.includes(variantId)) {
    element.remove();
    return;
  }

  // Recurse
  [...element.children].forEach((child) => filterDomByVariant(child, selectedVariantIds));
}

function CustomMTOSvg({
  svgUrl,
  selectedVariantIds,
  selectedData,
  className,
  type,
  variantsPreview,
}) {
  const [svgText, setSvgText] = React.useState(null);

  // Every id below lives in the document, not in the component: two previews rendered at the same
  // time (the cart thumbnails next to the product page) would otherwise share them, and an SVG
  // reference resolves to the first match in the document. A dome ring in the cart was making the
  // initial necklace follow its curve.
  const uid = React.useId().replace(/:/g, "");
  const curveId = `curve-${uid}`;
  const innerShadowId = `inner-shadow-${uid}`;

  // Fetch SVG from URL
  React.useEffect(() => {
    if (!svgUrl) return;

    let canceled = false;

    async function fetchSvg() {
      try {
        const res = await fetch(svgUrl);
        const text = await res.text();
        if (!canceled) setSvgText(text);
      } catch (e) {
        if (!canceled) setSvgText(null);
      }
    }

    fetchSvg();
    return () => {
      canceled = true;
    };
  }, [svgUrl]);

  // Filter the loaded SVG based on selected variants
  const filteredSvg = React.useMemo(() => {
    if (!svgText) return null;
    if (!isBrowser) return svgText;

    const parser = new DOMParser();
    const doc = parser.parseFromString(svgText, "image/svg+xml");
    const svgEl = doc.documentElement;

    // Apply variant filtering
    filterDomByVariant(svgEl, selectedVariantIds);

    // Apply className if provided
    if (className) {
      const prev = svgEl.getAttribute("class") || "";
      svgEl.setAttribute("class", `${prev} ${className}`.trim());
    }

    const serializer = new XMLSerializer();
    return serializer.serializeToString(svgEl);
  }, [svgText, selectedVariantIds, className]);

  if (!filteredSvg) return null;
  const engravedText = formatEngravingText(
    selectedData?.text ?? selectedData?.char?.char ?? "",
    selectedData?.font?.font_family
  );
  // The chains framing a nameplate come from the previews, which a product can be missing entirely
  let leftChainUrl = null;
  let rightChainUrl = null;

  if (type === "nameplate") {
    leftChainUrl = variantsPreview?.find((v) => v.title?.includes("left"))?.file?.url || null;
    rightChainUrl = variantsPreview?.find((v) => v.title?.includes("right"))?.file?.url || null;
  }

  if (engravedText) {
    return (
      <div className="relative overflow-hidden" style={{ containerType: "inline-size" }}>
        <div className="flex" dangerouslySetInnerHTML={{ __html: filteredSvg }} />
        {type === "nameplate" && (
          <div className="absolute top-0 flex w-full justify-center h-full">
            {leftChainUrl && (
              <img
                className={cn(
                  "-mr-[5px]",
                  selectedData?.font?.font_family === "Reg-MiniBlock" && "mr-[10px]"
                )}
                alt="Left Chain Nameplate"
                src={leftChainUrl}
              />
            )}
            <span
              style={{
                fontFamily: selectedData?.font?.font_family || "",
                ...(selectedData?.font?.style || {}),
              }}
              className="text-[48px] leading-none z-[2] align-center mt-[27.5%] tracking-[-3px] pr-[3px] bg-[linear-gradient(30deg,#F9E1B5_0%,#CFA55A_100%)] bg-clip-text text-transparent"
            >
              {engravedText}
            </span>
            {rightChainUrl && (
              <img
                className={cn(
                  "-ml-[5px]",
                  selectedData?.font?.font_family === "Reg-MiniBlock" && "ml-[10px]"
                )}
                alt="Right Chain Nameplate"
                src={rightChainUrl}
              />
            )}
          </div>
        )}
        {type === "dome_engraving" && (
          <svg
            className="w-full h-full absolute top-0 left-0"
            width={DOME_ARTBOARD.width}
            height={DOME_ARTBOARD.height}
            viewBox={`0 0 ${DOME_ARTBOARD.width} ${DOME_ARTBOARD.height}`}
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <defs>
              <filter id={innerShadowId} x="-20%" y="-20%" width="140%" height="140%">
                <feComponentTransfer in="SourceAlpha">
                  <feFuncA type="table" tableValues="1 0" />
                </feComponentTransfer>
                <feGaussianBlur stdDeviation="2" />
                <feOffset dx="0" dy="0" result="offsetblur" />
                <feFlood floodColor="#000000" />
                <feComposite in2="offsetblur" operator="in" />
                <feComposite in2="SourceAlpha" operator="in" />
                <feMerge>
                  <feMergeNode in="SourceGraphic" />
                  <feMergeNode />
                </feMerge>
              </filter>
            </defs>

            <path
              id={curveId}
              // "M132.855 106.476C178 67.1338 259.261 65.2017 311.5 103.899" CLASSIC + GOTHIC
              // "M132.855 109.476C178 70.1338 259.261 68.2017 311.5 106.899" MODERN
              d={
                selectedData?.font?.curve ||
                "M132.855 109.476C178 70.1338 259.261 68.2017 311.5 106.899"
              }
              stroke="transparent"
            />

            <text width="138" style={{ filter: `url(#${innerShadowId})` }}>
              <textPath
                className="leading-none"
                style={{
                  fontFamily: selectedData?.font?.font_family,
                  fontSize: toArtboardUnits(selectedData?.font?.style?.fontSize) || "30px",
                  fill: "#595959",
                }}
                xlinkHref={`#${curveId}`}
              >
                {engravedText}
              </textPath>
            </text>
          </svg>
        )}

        {(type === "single_engraving" || type === "back_engraving") && (
          <>
            <svg width="0" height="0" style={{ position: "absolute" }}>
              <defs>
                <filter id={innerShadowId} x="-50%" y="-50%" width="200%" height="200%">
                  <feComponentTransfer in="SourceAlpha">
                    <feFuncA type="table" tableValues="1 0" />
                  </feComponentTransfer>
                  <feGaussianBlur stdDeviation="2.58" />
                  <feOffset dx="0" dy="0" result="offsetblur" />
                  {selectedData?.font?.style?.color === "#C9C9C9" ? (
                    <feFlood floodColor="#8B8B8B" result="color" />
                  ) : (
                    <feFlood floodColor="#DEAD72" result="color" />
                  )}
                  <feComposite in2="offsetblur" operator="in" />
                  <feComposite in2="SourceAlpha" operator="in" />
                  <feMerge>
                    <feMergeNode in="SourceGraphic" />
                    <feMergeNode />
                  </feMerge>
                </filter>
              </defs>
            </svg>

            <span
              className="absolute left-1/2 -translate-x-1/2 leading-none whitespace-nowrap gold-text"
              style={{
                top: "65%",
                fontSize: "34px",
                ...(typeof selectedData?.font?.style === "object" ? selectedData.font.style : {}),
                fontFamily: selectedData?.font?.font_family || "",
                filter: selectedData?.font?.hasInnerShadow ? `url(#${innerShadowId})` : "",
                width: "100%",
                textAlign: "center",
              }}
            >
              {engravedText}
            </span>
          </>
        )}

        {type === "initial_necklace" && (
          <svg
            className="w-full h-full absolute top-0 left-0"
            width="445"
            height="178"
            viewBox="0 0 445 178"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <CurvedText
              word={engravedText}
              pathId={curveId}
              path="M-5.68292e-07 70.0005C107 95.5 195 98.5 277 92.5001C329.5 87.5 380 81 445 63.5"
              targetWidth={engravedText.length > 4 ? 380 : engravedText.length === 2 ? 220 : 280}
            />
          </svg>
        )}
      </div>
    );
  }
  return <div className="flex" dangerouslySetInnerHTML={{ __html: filteredSvg }} />;
}

export default withALErrorBoundary({
  name: "CustomMTOSvg",
  priority: "P2",
})(CustomMTOSvg);
